import { Divider } from '@katalon-studio/katalon-ui/v2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Page } from '../../components/page';
import { LoadingProgress } from '../../layout';
import {
  ActionStatus,
  Feature,
  PlanInterval,
  SingleProductPromotionInfo,
  Subscription,
  SubscriptionSource,
} from '../../models';
import { useQuery } from '../../routes';
import {
  fromAccounts,
  fromAuth,
  fromPaymentMethod,
  fromPromotion,
  fromSubscription,
  fromSurvey,
  useAppDispatch,
} from '../../store';
import { sendTrackingData } from '../../utils/utils';
import ButtonToLicenseManagement from '../payment-status/ButtonToLicenseManagement';
import { AddMorePlan } from './component/AddMorePlan';
import { AIChatbot } from './component/AIChatbot';
import { DetailSubscriptionGroup } from './component/DetailSubscriptionGroup';
import { DownloadKSGroup } from './component/DownloadKSGroup';
import { EmptySubscriptionGroup } from './component/EmptySubsriptionGroup';
import { withSubscriptions } from '../../utils/analytics';
import { PromotionSection } from './component/promotion/PromotionSection';
import { useGetTrackingCookies } from '../../hook';
import { useLaunchDarkly } from '../../launchdarkly';
import { TruePlatformUpgrade } from './component/true-platform/TruePlatformUpgrade';
import trueDirectSaleFeature = Feature.trueDirectSaleFeature;
import selfServeFeature = Feature.selfServeFeature;
import directSaleFeature = Feature.directSaleFeature;

export const SubscriptionLegacy = () => {
  const dispatch = useAppDispatch();
  const { get } = useQuery();
  const { flags } = useLaunchDarkly();
  const accountId = Number(get('accountId'));
  const ftic = get('ftic') === 'true';
  const user = useSelector(fromAuth.selectUser);
  const subscriptions = useSelector(fromSubscription.selectByAccountId(accountId));
  const paidSubscriptions = useSelector(
    fromSubscription.selectSubscriptionsBySource(SubscriptionSource.RECURLY),
  );
  const subscriptionLoading = useSelector(fromSubscription.selectLoading);
  const hadSubscription = useSelector(fromSubscription.selectHadSubscription);
  const surveyStatus = useSelector(fromSurvey.selectSurveyStatus);
  const promotionInfo = useSelector(fromPromotion.selectActivePromotion);
  const promotion = promotionInfo as SingleProductPromotionInfo;
  const hasTrackedViewRef = useRef(false);
  const showPromotion = !!promotion
    && promotion.timeWindow !== null
    && promotion.timeWindow?.startDate!! < Date.now()
    && promotion.timeWindow?.endDate!! > Date.now();

  const showUpgradeBanner = (ftic && flags?.isDLT)
    || (flags?.trueUpgradeEnabled
      && paidSubscriptions.length > 0
      && !paidSubscriptions.some(
        sub => sub.feature === Feature.TRUE_TEAM
          || sub.feature === Feature.TRUE_ENTERPRISE
          || sub.billingInterval !== PlanInterval.YEAR,
      ));

  const expiryDate = useMemo(() => {
    const sub = paidSubscriptions
      .reduce<Subscription | null>(
      (latest, current) => (latest === null || current > latest ? current : latest),
      null,
    );
    if (!sub) {
      return undefined;
    }

    const renewalWindow = flags?.truePlatformConfig?.renewalWindow;
    if (!renewalWindow) {
      return undefined;
    }

    const windowDeadline = moment().endOf('month').add(renewalWindow, 'months');

    return moment(sub.expiryDate).isBefore(windowDeadline)
      ? new Date(sub.expiryDate) : undefined;
  }, [paidSubscriptions]);

  const trueUpgradeInfo = useSelector(fromSubscription.selectTrueUpgradeInfo);
  const fetchResource = async () => {
    if (accountId) {
      await Promise.all([
        dispatch(fromAccounts.doCheckFirstThreeLicenses()),
        dispatch(fromPaymentMethod.doGetDefaultPaymentMethod()),
        dispatch(fromAccounts.doGetSalesforceLinkedAccount()),
        dispatch(fromPromotion.doFetchActivePromotion()),
      ]);
    }
  };

  const hideAddMore = () => {
    if (trueUpgradeInfo?.reserved) {
      return true;
    }

    const legacySelfServe = selfServeFeature.every(
      feature => subscriptions.some(sub => sub.feature === feature),
    );
    const legacyDirectServe = directSaleFeature.every(
      feature => subscriptions.some(sub => sub.feature === feature),
    );
    const trueDirectServe = trueDirectSaleFeature.every(
      feature => subscriptions.some(sub => sub.feature === feature),
    );

    if (legacySelfServe && legacyDirectServe) {
      return true;
    }

    return !!trueDirectServe;
  };

  const trackingCookies = useGetTrackingCookies();
  useEffect(() => {
    if (hasTrackedViewRef.current || subscriptionLoading) {
      return;
    }

    hasTrackedViewRef.current = true;

    sendTrackingData(
      'view_subscription_management',
      accountId,
      undefined,
      withSubscriptions(
        {
          page: 'subscription_management',
          ...showUpgradeBanner
            ? { upgradeButtonName: expiryDate ? 'Upgrade at Next Renewal' : 'Upgrade Immediately' }
            : {},
          ...trackingCookies,
        },
        subscriptions,
      ),
    );
  }, [accountId, subscriptions, subscriptionLoading]);

  return (
    <Page
      id="subscription"
      headerId="page.subscription.header"
      headerValue={{
        userName: user?.firstName,
      }}
      fetchData={fetchResource}
    >
      {/* Promotion Campaign Section */}
      {showPromotion && <PromotionSection accountId={accountId} />}

      {/* True Team Upgrade Banner */}
      {showUpgradeBanner && (
        <Box mt={2}>
          <TruePlatformUpgrade
            accountId={accountId}
            renewalDate={expiryDate}
          />
        </Box>
      )}

      <Box
        id="current-subscription"
        key="current-subscription"
        display="flex"
        flexDirection="column"
        flexWrap="wrap"
        paddingY={2}
      >
        <Box
          id="current-subscription-header"
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
        >
          <Typography
            fontWeight={600}
            fontSize={24}
            color="#43509B"
          >
            <FormattedMessage id="page.subscription.current.subscription.title" />
          </Typography>
          {subscriptions.length > 0 && (
            <ButtonToLicenseManagement
              title="page.subscription.manage.license.button"
              width={220}
              height={40}
              fontWeight={700}
              marginRight={1}
            />
          )}
        </Box>
        {subscriptions.length > 0
          ? (
            <DetailSubscriptionGroup subscriptions={subscriptions} />
          )
          : (hadSubscription
            ? <EmptySubscriptionGroup />
            : <DownloadKSGroup />
          )}
      </Box>
      <Box paddingY={2}>
        <Divider />
      </Box>
      {!hideAddMore() && (
        <Box
          id="current-subscription-add-more"
          key="current-subscription-add-more"
          display="flex"
          flexDirection="column"
          flexWrap="wrap"
          paddingY={2}
        >
          <Typography
            fontWeight={600}
            fontSize={24}
            color="#43509B"
          >
            <FormattedMessage id="page.subscription.add.more.plan.title" />
          </Typography>
          <AddMorePlan subscriptions={subscriptions} />
        </Box>
      )}
      {(subscriptionLoading || surveyStatus === ActionStatus.LOADING)
        && (<LoadingProgress fullscreen />)}
      <AIChatbot />
    </Page>
  );
};
